import React, { useState } from 'react';

const SAMPLE_HTML = `<!DOCTYPE html>
<html>
<body>
  <div class="header">
    <div class="logo">My Site</div>
    <div class="nav">
      <div class="link">Home</div>
      <div class="link">About</div>
    </div>
  </div>
  <div class="main-content">
    <div class="big-title">Welcome</div>
    <div class="post">
      <div class="bold">Latest News</div>
      <div>Some text here...</div>
      <div class="btn" onclick="submit()">Read More</div>
    </div>
  </div>
  <div class="footer">Copyright 2024</div>
</body>
</html>`;

interface AnalyzeFormProps {
  busy: boolean;
  busyLabel?: string;
  onSubmitHtml: (html: string) => void;
  onSubmitUrl: (url: string) => void;
}

type Mode = 'url' | 'html';

export const AnalyzeForm: React.FC<AnalyzeFormProps> = ({
  busy,
  busyLabel,
  onSubmitHtml,
  onSubmitUrl,
}) => {
  const [mode, setMode] = useState<Mode>('url');
  const [url, setUrl] = useState('');
  const [html, setHtml] = useState('');

  const submit = () => {
    if (busy) return;
    if (mode === 'url') {
      const trimmed = url.trim();
      if (!trimmed) return;
      onSubmitUrl(trimmed);
    } else {
      const trimmed = html.trim();
      if (!trimmed) return;
      onSubmitHtml(trimmed);
    }
  };

  return (
    <div className="card">
      <div className="tabs" role="tablist" aria-label="Input mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'url'}
          className={`tab ${mode === 'url' ? 'is-active' : ''}`}
          onClick={() => setMode('url')}
        >
          Analyze a URL
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'html'}
          className={`tab ${mode === 'html' ? 'is-active' : ''}`}
          onClick={() => setMode('html')}
        >
          Paste HTML
        </button>
      </div>

      {mode === 'url' ? (
        <div>
          <label className="eyebrow" htmlFor="url-input" style={{ display: 'block', marginBottom: 8 }}>
            URL
          </label>
          <input
            id="url-input"
            type="url"
            className="input"
            placeholder="https://example.com/article"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="meta" style={{ marginTop: 8 }}>
            We render the page (JS executed) via Firecrawl, then analyze the rendered HTML.
          </p>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <label className="eyebrow" htmlFor="html-input">
              Rendered HTML
            </label>
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: 'transparent', color: 'var(--signal)', padding: '4px 0' }}
              onClick={() => setHtml(SAMPLE_HTML)}
            >
              Load div-soup example
            </button>
          </div>
          <textarea
            id="html-input"
            className="textarea"
            placeholder="<!-- Paste rendered HTML, e.g. document.documentElement.outerHTML -->"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            spellCheck={false}
          />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={busy || (mode === 'url' ? !url.trim() : !html.trim())}
        >
          {busy ? (
            <>
              <span className="spinner" />
              {busyLabel || 'Working…'}
            </>
          ) : (
            <>
              Run analysis
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
